import {
  appendIsTravelAppParam,
  COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT,
  COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION,
  COMPLIMENTARY_NIGHT_PROMOTION_TITLE,
  getCompNightTermsUrl,
  getConfigTenant,
  getPortalName,
  isCorpTenant,
  PATH_PREMIUM_OFFERS_SHOP,
  useIsTravelApp,
  useQueryParams,
} from "@capone/common";
import {
  LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS,
  PREMIER_COLLECTION_EXPERIMENT,
} from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  GENERIC_HOTEL_PROMOTION_OFFER_IMAGE,
  getTravelOfferCardContent,
  isOfferHSP,
  isOfferHSPTiered,
  ProductLargeImageBanner,
  TravelOfferCard,
  TravelOfferCardMinimal,
  TravelSalesEventBanner,
  TravelWalletDetailsModal,
  useDeviceTypes,
} from "halifax";
import React, { useCallback, useEffect, useState } from "react";
import { InView } from "react-intersection-observer";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  ENGAGED_OFFER_CTA,
  Funnel,
  SELECTED_TRAVEL_OFFER,
  SelectedTravelOfferScreen,
  TravelWalletOffer,
  VIEW_CREDIT_HISTORY,
  VIEWED_OFFERS_LIST,
  VIEWED_PREMIUM_OFFER_PAGE,
  VIEWED_TRAVEL_OFFER,
  ViewedCompNightBannerEntryPoint,
  ViewedPremiumOfferPageProperties,
  MediaEventType,
  MediaPlacement,
  VIEWED_COMP_NIGHT_BANNER,
  ViewedCompNightBannerProperties,
} from "redmond";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import { trackMediaEvent } from "../../../../../../../cap1-application/b2b-base/src/api/v1/trackHTSMedia";
import { useViewportObserver } from "../../../../../../../cap1-application/b2b-base/src/hooks/useViewportObserver";
import {
  AVAILABLE,
  CONTROL,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_SALE,
  TRAVEL_SALE_ACTIVE,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_VARIANTS,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { CallState } from "../../../../modules/rewards/reducer";
import { PATH_TRAVEL_SALE } from "../../../../utils/paths";
import * as textConstants from "../../textConstants";
import {
  PATH_CARS_HOME,
  PATH_FLIGHTS_HOME,
  PATH_HOTELS_FLIGHTS,
  PATH_HOTELS_HOME,
  PATH_PREMIUM_STAYS,
  PATH_PREMIUM_VACATION_RENTALS,
} from "../../utils/paths";
import { OffersListConnectorProps } from "./container";
import "./styles.scss";

export interface IOffersListProps
  extends OffersListConnectorProps,
    RouteComponentProps {}

export const OffersList = ({
  allOffers,
  activeOffers,
  areOfferDetailsLoading,
  fetchRewardsAccountsCallState,
}: IOffersListProps) => {
  const expState = useExperiments();
  const { matchesMobile } = useDeviceTypes();
  const { isTravelApp } = useIsTravelApp();
  const params = useQueryParams();

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT,
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment],
  );

  const travelSalesEventVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );

  const showComplimentaryNightPromotionEntrypoints =
    getExperimentVariant(
      expState.experiments,
      LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS,
    ) === AVAILABLE;

  const showsPremierCollection =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT,
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const [selectedOffer, setSelectedOffer] = useState<
    | {
        offer: TravelWalletOffer;
        index?: number;
      }
    | undefined
  >(undefined);

  const history = useHistory();

  const offers =
    travelSalesEventVariant === TRAVEL_SALE_LEAD_UP ? activeOffers : allOffers;

  // Find the last HSP offer so we can render the complimentary night banner after it
  const lastHSPIndex = offers.reduce(
    (lastIndex, currentOffer, currentIndex) =>
      isOfferHSP(currentOffer.descriptions) ? currentIndex : lastIndex,
    -1,
  );

  // Whether the complimentary night banner renders at the top of the list
  // (i.e. no HSP offers exist, including when the user has no offers at all)
  const willRenderComplimentaryNightBannerAtStart =
    travelSalesEventVariant !== TRAVEL_SALE_ACTIVE &&
    showComplimentaryNightPromotionEntrypoints &&
    !matchesMobile &&
    lastHSPIndex === -1;

  const renderComplimentaryNightBanner = () => (
    <InView
      as="div"
      triggerOnce
      className="desktop-offer-list-complimentary-night-promotion-wrapper"
      onChange={(inView) => {
        if (inView) {
          trackEvent({
            eventName: VIEWED_COMP_NIGHT_BANNER,
            properties: {
              step: ViewedCompNightBannerEntryPoint.MY_TRAVEL_CREDITS_OFFERS,
            } as ViewedCompNightBannerProperties,
          });
        }
      }}
    >
      <ProductLargeImageBanner
        imageUrl={GENERIC_HOTEL_PROMOTION_OFFER_IMAGE}
        title={COMPLIMENTARY_NIGHT_PROMOTION_TITLE}
        description={COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION(
          showsPremierCollection,
        )}
        buttonText={COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT}
        buttonAction={() => {
          trackEvent({
            eventName: VIEWED_PREMIUM_OFFER_PAGE,
            properties: {
              entry_point:
                ViewedCompNightBannerEntryPoint.MY_TRAVEL_CREDITS_OFFERS,
            } as ViewedPremiumOfferPageProperties,
          });
          history.push(
            appendIsTravelAppParam(PATH_PREMIUM_OFFERS_SHOP, isTravelApp),
          );
        }}
        termsAndConditionsUrl={getCompNightTermsUrl()}
      />
    </InView>
  );

  const getOfferCTAs = useCallback(
    (
      offer: TravelWalletOffer,
      includeViewOfferDetailsCTA?: boolean,
      offerRowIndex?: number,
    ) => {
      const offerIndexToUse =
        offerRowIndex !== undefined ? offerRowIndex + 1 : undefined;
      const trackOfferEngagementEvent = () => {
        trackEvent({
          eventName: ENGAGED_OFFER_CTA,
          properties: {
            location: SelectedTravelOfferScreen.TRAVEL_WALLET_LANDING,
            entry_type:
              matchesMobile && !includeViewOfferDetailsCTA ? "modal" : "card",
            index_row_id: offerIndexToUse,
            funnel: offer.funnels.join(","),
          },
          encryptedProperties: [
            offer.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        });

        trackMediaEvent({
          eventType: MediaEventType.Click,
          trackingProperties: offer?.trackingPropertiesV2,
          placement: MediaPlacement.TravelCreditsAndOffersPage,
        });
      };
      const { funnels } = offer;
      // Sort funnels to move Home/Homes to the end (has longest text so it should take full width if needed)
      const sortedFunnels = [...funnels].sort((a, b) => {
        const isAHome = a === Funnel.Home || a === Funnel.Homes;
        const isBHome = b === Funnel.Home || b === Funnel.Homes;
        if (isAHome && !isBHome) return 1;
        if (!isAHome && isBHome) return -1;
        return 0;
      });

      const hasHomeFunnel = funnels.some(
        (f) => f === Funnel.Home || f === Funnel.Homes,
      );
      const shouldMakeSecondToLastFullWidth =
        matchesMobile &&
        sortedFunnels.length % 2 === 0 &&
        hasHomeFunnel &&
        sortedFunnels.length >= 2;

      const funnelButton = sortedFunnels.map((funnel, index) => {
        const isSecondToLast = index === sortedFunnels.length - 2;
        const isHome = funnel === Funnel.Home || funnel === Funnel.Homes;
        const shouldBeFullWidth =
          isHome || (shouldMakeSecondToLastFullWidth && isSecondToLast);

        switch (funnel) {
          case Funnel.Air:
            return {
              text: "Search flights",
              onClick: () => {
                history.push(PATH_FLIGHTS_HOME);
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Lodging:
            if (
              isCorpTenant(getConfigTenant()) &&
              offer.descriptions &&
              offer.descriptions[0].toLowerCase().includes("hotels + flights")
            ) {
              return {
                text: "Search hotels + flights",
                onClick: () => {
                  history.push(PATH_HOTELS_FLIGHTS);
                  trackOfferEngagementEvent();
                },
                fullWidth: shouldBeFullWidth,
              };
            }
            if (
              offer.descriptions &&
              (offer.descriptions[0]
                .toLowerCase()
                .includes("premier collection") ||
                offer.descriptions[0]
                  .toLowerCase()
                  .includes("premier or lifestyle collection") ||
                offer.descriptions[0]
                  .toLowerCase()
                  .includes("lifestyle collection"))
            ) {
              return {
                text: "Search premium stays",
                onClick: () => {
                  history.push(PATH_PREMIUM_STAYS);
                  trackOfferEngagementEvent();
                },
                fullWidth: shouldBeFullWidth,
              };
            }
            return {
              text: "Search hotels",
              onClick: () => {
                history.push(PATH_HOTELS_HOME);
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Homes:
          case Funnel.Home:
            return {
              text: "Search vacation rentals",
              onClick: () => {
                history.push(PATH_PREMIUM_VACATION_RENTALS);
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Ground:
            return {
              text: "Search cars",
              onClick: () => {
                history.push(PATH_CARS_HOME);
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Experiences:
          case Funnel.Activity:
            return {
              text: "Search activities",
              onClick: () => {
                history.push("/activities/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
          case Funnel.Package:
            return {
              text: "Search packages",
              onClick: () => {
                history.push("/packages/");
                trackOfferEngagementEvent();
              },
              fullWidth: shouldBeFullWidth,
            };
        }
      });
      return [
        ...funnelButton,
        ...(includeViewOfferDetailsCTA
          ? [
              {
                text: "View offer details",
                onClick: () => {
                  setSelectedOffer({ offer, index: offerIndexToUse });
                  trackEvent({
                    eventName: SELECTED_TRAVEL_OFFER,
                    properties: {
                      screen: SelectedTravelOfferScreen.TRAVEL_WALLET_LANDING,
                      offer_count: offers.length,
                      ...offer.trackingProperties,
                    },
                  });

                  trackOfferEngagementEvent();
                },
              },
            ]
          : []),
      ];
    },
    [history],
  );

  useEffect(() => {
    if (fetchRewardsAccountsCallState === CallState.Success) {
      const bestOverallOffer = [...offers].sort(
        (a, b) => a.amount.amount - b.amount.amount,
      )[0];

      trackEvent({
        eventName: VIEWED_OFFERS_LIST,
        properties: {
          has_offer: offers.length > 0,
          offer_count: offers.length,
          entry_point: params.entry_point,
          ...bestOverallOffer?.trackingProperties,
        },
      });
    }
  }, [offers, fetchRewardsAccountsCallState]);

  const TravelOfferCardWithTracking = ({
    offer,
    ctas,
    ctasForModal,
  }: {
    offer: TravelWalletOffer;
    ctas: ReturnType<typeof getOfferCTAs>;
    ctasForModal?: ReturnType<typeof getOfferCTAs>;
  }) => {
    const offerContent = getTravelOfferCardContent(offer);
    const offerLocation =
      offer.descriptions.length >= 3
        ? offer?.descriptions[2]
        : offer.descriptions[0];

    const viewportObserver = useViewportObserver<HTMLDivElement>(() => {
      trackMediaEvent({
        eventType: MediaEventType.Impression,
        trackingProperties: offer?.trackingPropertiesV2,
        placement: MediaPlacement.TravelCreditsAndOffersPage,
      });
    });

    return (
      <div ref={viewportObserver.ref as React.RefObject<HTMLDivElement>}>
        <InView
          key={offer.id}
          as="div"
          onChange={(inView) => {
            if (inView) {
              trackEvent({
                eventName: VIEWED_TRAVEL_OFFER,
                properties: {
                  location: "travel-credits-and-offers-page",
                },
                encryptedProperties: [
                  offer?.trackingPropertiesV2?.encryptedProperties ?? "",
                ],
              });
            }
          }}
          threshold={0.8}
        >
          {matchesMobile ? (
            <TravelOfferCardMinimal
              key={offer.id}
              title={
                isOfferHSPTiered(offer.descriptions)
                  ? `<strong>${offer.shopPageBanner}</strong>` || offer.title
                  : offer.title
              }
              expirationString={offerContent.expirationDetail}
              validityString={offerContent.applicabilityDetail}
              ctas={ctas}
              offerFunnels={offer.funnels}
              offerLocation={offerLocation}
              imageId={offer.imageId}
              imageFolderUrl={offer.imageFolderUrl}
            />
          ) : (
            <Box className="offers-list-card-with-heading" key={offer.id}>
              <TravelOfferCard
                offerContent={offerContent}
                showAccordian={false}
                ctas={ctasForModal || ctas}
                buttonClassName="b2b"
                offerFunnels={offer.funnels}
                offerLocation={offerLocation}
                imageId={offer.imageId}
                imageFolderUrl={offer.imageFolderUrl}
                titleLabelText={
                  isOfferHSPTiered(offer.descriptions)
                    ? textConstants.HSP_OFFER_TITLE_LABEL
                    : undefined
                }
              />
            </Box>
          )}
        </InView>
      </div>
    );
  };

  return (
    <Box className={clsx("offers-list-container", { mobile: matchesMobile })}>
      {isTravelWalletCreditsExperiment && !matchesMobile && (
        <Box
          className={clsx("offers-heading-container", {
            mobile: matchesMobile,
          })}
        >
          <Typography variant="h2" className="offers-heading-title">
            <strong>Travel offers</strong>
            {offers.length > 0 && <strong> ({offers.length})</strong>}
          </Typography>
          <Typography className="offers-heading-description">
            Travel offers are limited-time deals issued by Capital One that are
            applicable on future travel bookings.
          </Typography>
        </Box>
      )}
      <Box className={clsx("offers-list-cards", { mobile: matchesMobile })}>
        {!areOfferDetailsLoading &&
          offers.length === 0 &&
          !willRenderComplimentaryNightBannerAtStart && (
            <TravelOfferCardMinimal
              title="You don't have any travel offers."
              body="We'll notify you when travel offers become available."
              ctas={[
                {
                  text: `Explore ${getPortalName()}`,
                  onClick: () => history.push("/"),
                },
              ]}
            />
          )}
        {/* Render banner at the beginning if no HSP offers exist (including
            when the user has no offers at all) */}
        {willRenderComplimentaryNightBannerAtStart &&
          renderComplimentaryNightBanner()}
        {travelSalesEventVariant === TRAVEL_SALE_ACTIVE && !matchesMobile && (
          <TravelSalesEventBanner
            variant={"default"}
            onClick={() =>
              matchesMobile
                ? history.push(PATH_TRAVEL_SALE)
                : window.open(PATH_TRAVEL_SALE, "_blank")
            }
            subtitle={textConstants.TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE}
            buttonText={textConstants.TRAVEL_SALES_EVENT_ACTIVE_CTA}
            className={clsx("travel-sale-entry-point")}
          />
        )}
        {matchesMobile && selectedOffer && (
          <TravelWalletDetailsModal
            openModal={!!selectedOffer}
            setOpenModal={() => setSelectedOffer(undefined)}
            offerCardProps={{
              offerContent: getTravelOfferCardContent(selectedOffer.offer),
              ctas: getOfferCTAs(
                selectedOffer.offer,
                undefined,
                selectedOffer.index,
              ),
              buttonClassName: "b2b",
              titleLabelText: isOfferHSPTiered(selectedOffer.offer.descriptions)
                ? textConstants.HSP_OFFER_TITLE_LABEL
                : undefined,
              className: clsx({ xmod: isXmodRedesign }),
            }}
            termsType={matchesMobile ? "accordion" : "link"}
            isMobile
            offerFunnels={selectedOffer.offer.funnels}
            offerLocation={selectedOffer.offer.descriptions[2]}
            isTravelSale={travelSalesEventVariant !== CONTROL}
            imageId={selectedOffer.offer.imageId}
            imageFolderUrl={selectedOffer.offer.imageFolderUrl}
            onViewCreditHistory={() =>
              trackEvent({
                eventName: VIEW_CREDIT_HISTORY,
                properties: {},
              })
            }
          />
        )}
        {travelSalesEventVariant !== TRAVEL_SALE_ACTIVE &&
          offers.flatMap((offer, i) => {
            // Check if we should render the complimentary night banner after this offer
            const shouldRenderBannerAfter =
              showComplimentaryNightPromotionEntrypoints &&
              !matchesMobile &&
              i === lastHSPIndex;

            const offerCard = (
              <TravelOfferCardWithTracking
                key={offer.id}
                offer={offer}
                ctas={getOfferCTAs(offer, true, i)}
                ctasForModal={getOfferCTAs(offer, undefined, i)}
              />
            );

            return shouldRenderBannerAfter
              ? [
                  offerCard,
                  <React.Fragment key="complimentary-night-banner">
                    {renderComplimentaryNightBanner()}
                  </React.Fragment>,
                ]
              : [offerCard];
          })}
      </Box>
    </Box>
  );
};
